import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
const RtbAd = ({ ad, zoneId, width, height, adBidId}) => {
  const [impressionTracked, setImpressionTracked] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const adRef = useRef(null);
  const containerRef = useRef(null);

  // Fonction utilitaire pour normaliser les dimensions
  const normalizeDimension = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Supprimer "px" et convertir en nombre
      return parseInt(value.replace('px', ''), 10);
    }
    return null;
  };

  // Déterminer si c'est un pixel de tracking (1x1)
  const isTrackingPixel = useMemo(() => {
    // Vérifier les props width/height
    const normalizedWidth = normalizeDimension(width);
    const normalizedHeight = normalizeDimension(height);
    const propsIsTracking = normalizedWidth === 1 && normalizedHeight === 1;

    // Vérifier ad.ad.w et ad.ad.h
    const adWidth = ad.ad?.w ? normalizeDimension(ad.ad.w) : null;
    const adHeight = ad.ad?.h ? normalizeDimension(ad.ad.h) : null;
    const adDataIsTracking = adWidth === 1 && adHeight === 1;

    return propsIsTracking || adDataIsTracking;
  }, [width, height, ad.ad]);

  // Extraction des données de l'annonce selon la structure OpenRTB
  const adData = useMemo(() => {
    // Si c'est une structure OpenRTB complète
    if (ad.seatbid && ad.seatbid[0] && ad.seatbid[0].bid && ad.seatbid[0].bid[0]) {
      return ad.seatbid[0].bid[0];
    }
    // Si c'est déjà l'objet bid direct
    else if (ad.adm || ad.price || ad.id) {
      return ad;
    }
    // Si c'est l'ancienne structure avec rtb_ad
    else if (ad.rtb_ad) {
      return ad.rtb_ad;
    }
    // Fallback
    return ad;
  }, [ad]);

  console.log("Ad in RtbAd:", ad);
  console.log("Extracted adData:", adData);
  console.log("Props dimensions:", { width, height });
  console.log("Ad data dimensions:", { w: ad.ad?.w, h: ad.ad?.h });
  console.log("Is tracking pixel (1x1):", isTrackingPixel);
  console.log("Impression tracked:", impressionTracked);
  console.log("AdData exists:", !!adData);
  console.log("AdData.adm exists:", !!adData?.adm);

  // Fonction pour tracker l'impression
  const trackImpression = async () => {
    try {
      if (!impressionTracked && (isVisible || isTrackingPixel)) {
        console.log("Tracking impression for ad:", adData.id || adData.adid, isTrackingPixel ? "(tracking pixel)" : "(visible ad)");
        setImpressionTracked(true);
        
        // Appel SDK pour tracker l'impression
        var imp = {
          adId: 0,
          adBidId: adBidId || null,
          mediaId: 0,
          zoneId: zoneId,
          price: adData.price || 0,
          refererUrl: window.location.href
        }
        console.log("imp to add", imp);
        await sdk.models.AdsImpression.addAdImpression(imp);

        // Si il y a une URL de notification d'impression (nurl)
        if (adData.nurl) {
          try {
            // Remplacer les macros OpenRTB courantes
            let notificationUrl = adData.nurl
              .replace('${AUCTION_PRICE}', adData.price || 0)
              .replace('%%WINNING_PRICE%%', adData.price || 0)
              .replace('${AUCTION_ID}', adData.id || adData.adid || '')
              .replace('%%AUCTION_ID%%', adData.id || adData.adid || '');

            // Envoyer la notification d'impression au DSP/SSP
            fetch(notificationUrl, { 
              method: 'GET',
              mode: 'no-cors' // Pour éviter les problèmes CORS
            }).catch(err => {
              console.warn('Erreur notification impression:', err);
            });
          } catch (error) {
            console.error('Erreur lors de l\'envoi de la notification:', error);
          }
        }
      }
    } catch (error) {
      console.error("Erreur lors du suivi de l'impression:", error);
    }
  };

  // Tracking immédiat pour les pixels 1x1
  useEffect(() => {
    if (isTrackingPixel && !impressionTracked && adData) {
      console.log("Tracking pixel detected - immediate impression tracking");
      // Ajouter un petit délai pour s'assurer que le composant est monté
      const timeoutId = setTimeout(() => {
        trackImpression();
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isTrackingPixel, impressionTracked, adData?.id, adData?.adid]);

  // Observer d'intersection pour détecter la visibilité (seulement pour les ads normales)
  useEffect(() => {
    // Ne pas utiliser l'intersection observer pour les pixels de tracking
    if (isTrackingPixel) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setIsVisible(visible);
        
        // Track impression quand 50% de l'ad est visible
        if (visible && !impressionTracked) {
          // Petit délai pour s'assurer que l'ad est vraiment visible
          setTimeout(() => {
            trackImpression();
          }, 100);
        }
      },
      { 
        threshold: 0.5, // 50% de l'élément doit être visible
        rootMargin: '0px 0px -50px 0px' // Marge pour être plus strict
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [adData, zoneId, impressionTracked, isTrackingPixel]);

  // Fonction pour injecter et exécuter le HTML avec JavaScript
  useEffect(() => {
    if (adRef.current && adData.adm) {
      const container = adRef.current;
      
      // Nettoyer le contenu précédent
      container.innerHTML = '';
      
      // Créer un conteneur temporaire pour parser le HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = adData.adm;
      
      // Traiter les scripts séparément pour les exécuter correctement
      const scripts = tempDiv.querySelectorAll('script');
      const nonScriptContent = tempDiv.cloneNode(true);
      
      // Supprimer les scripts du contenu HTML
      nonScriptContent.querySelectorAll('script').forEach(script => script.remove());
      
      // Injecter le HTML sans les scripts
      container.innerHTML = nonScriptContent.innerHTML;
      
      // Exécuter les scripts un par un
      scripts.forEach((script, index) => {
        try {
          if (script.src) {
            // Script externe
            const newScript = document.createElement('script');
            newScript.src = script.src;
            newScript.async = script.async || false;
            newScript.defer = script.defer || false;
            
            // Copier les autres attributs
            Array.from(script.attributes).forEach(attr => {
              if (attr.name !== 'src') {
                newScript.setAttribute(attr.name, attr.value);
              }
            });
            
            document.head.appendChild(newScript);
            
            // Nettoyer après un délai
            setTimeout(() => {
              if (newScript.parentNode) {
                newScript.parentNode.removeChild(newScript);
              }
            }, 10000);
            
          } else if (script.textContent || script.innerHTML) {
            // Script inline
            const scriptContent = script.textContent || script.innerHTML;
            
            // Remplacer les macros courantes dans les scripts
            let processedScript = scriptContent
              .replace(/%%IMP_ID%%/g, adData.id || adData.adid || '')
              .replace(/%%CLICK_ID%%/g, adData.id || adData.adid || '')
              .replace(/%%WINNING_PRICE%%/g, adData.price || 0)
              .replace(/\${AUCTION_PRICE}/g, adData.price || 0)
              .replace(/\${AUCTION_ID}/g, adData.id || adData.adid || '');
            
            // Créer et exécuter le script
            const newScript = document.createElement('script');
            newScript.textContent = processedScript;
            
            // Ajouter temporairement au DOM pour exécution
            container.appendChild(newScript);
            
            // Supprimer immédiatement après exécution
            setTimeout(() => {
              if (newScript.parentNode) {
                newScript.parentNode.removeChild(newScript);
              }
            }, 100);
          }
        } catch (error) {
          console.error(`Erreur lors de l'exécution du script ${index}:`, error);
        }
      });

      // Traiter les liens pour le tracking des clics (seulement pour les ads normales)
      if (!isTrackingPixel) {
        const links = container.querySelectorAll('a');
        links.forEach(link => {
          const originalHref = link.href;
          
          link.addEventListener('click', (e) => {
            try {
              // Tracker le clic
              console.log('Click tracked for ad:', adData.id || adData.adid);
              
              // Si il y a une URL de tracking de clic
              if (adData.clickUrl || link.href.includes('click')) {
                // Envoyer la notification de clic
                let clickUrl = adData.clickUrl || link.href;
                
                // Remplacer les macros
                clickUrl = clickUrl
                  .replace('%%CLICK_ID%%', adData.id || adData.adid || '')
                  .replace('${AUCTION_ID}', adData.id || adData.adid || '')
                  .replace('%%WINNING_PRICE%%', adData.price || 0);
                
                // Envoyer la notification (ne pas bloquer la navigation)
                fetch(clickUrl, { 
                  method: 'GET',
                  mode: 'no-cors'
                }).catch(err => console.warn('Erreur tracking clic:', err));
              }
              
              // Appel SDK pour tracker le clic
              sdk.models.AdsClick?.addAdClick?.({
                adId: adData.id || adData.adid || 0,
                zoneId: zoneId,
                price: (adData.price || 0) / 1000,
              }).catch(err => console.warn('Erreur SDK clic:', err));
              
            } catch (error) {
              console.error('Erreur lors du tracking du clic:', error);
            }
          });
        });
      }
    }
  }, [adData.adm, adData.id, adData.adid, zoneId, isTrackingPixel]);

  // Gestion des erreurs d'affichage
  const handleError = (error) => {
    console.error('Erreur lors du rendu de l\'annonce:', error);
  };

  // Si pas de données d'annonce, ne rien afficher
  if (!adData || !adData.adm) {
    console.warn('Aucune donnée d\'annonce disponible:', adData);
    return null;
  }

  return (
    <div 
      ref={containerRef}
      className="rtb-ad-container"
      onError={handleError}
      style={{
        width: isTrackingPixel ? '1px' : '100%',
        height: isTrackingPixel ? '1px' : 'auto',
        minHeight: isTrackingPixel ? '1px' : '50px',
        overflow: 'hidden',
        opacity: isTrackingPixel ? 0 : 1 // Rendre invisible les pixels de tracking
      }}
    >
      <div 
        ref={adRef}
        className="rtb-ad-content"
        style={{
          width: isTrackingPixel ? '1px' : '100%',
          height: isTrackingPixel ? '1px' : 'auto',
          minHeight: isTrackingPixel ? '1px' : '50px'
        }}
      />
      
      {/* Indicateur de debug en mode développement */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'absolute',
          top: '2px',
          right: '2px',
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          fontSize: '10px',
          padding: '2px 4px',
          borderRadius: '2px',
          pointerEvents: 'none',
          zIndex: 9999
        }}>
          {isTrackingPixel ? '📍' : (isVisible ? '👁️' : '📴')} {impressionTracked ? '✅' : '⏳'}
          <br />
          ID: {adData.id || adData.adid || 'N/A'}
          {isTrackingPixel && <br />}
          {isTrackingPixel && 'PIXEL'}
        </div>
      )}
    </div>
  );
};

export { RtbAd };